import { z } from "zod";
/**
 * Affordances: the device-declared UI vocabulary. A device's manifest declares
 * a list of affordances; BrewBase maps each affordance *type* to a widget and
 * composes the device page from them — with zero device-type knowledge in core.
 * (Named "affordance" to avoid colliding with RBAC "capabilities".)
 *
 * Each affordance is { id, type, refs (→ channel/config keys), params, group?,
 * order? }. `params` carries the rich, validated config options per type. This
 * module is the single source of truth for the type vocabulary, the per-type
 * param schemas, and the fixed icon allowlist; the web widget registry and the
 * birth-ingest validator both build on it.
 */
// ── Fixed icon vocabulary (Lucide names; brewery/natural/food + control) ──────
export const AFFORDANCE_ICON_NAMES = [
    "power",
    "play",
    "pause",
    "square",
    "droplet",
    "droplets",
    "gauge",
    "waves",
    "wind",
    "thermometer",
    "activity",
    "zap",
    "rotate-cw",
    "rotate-ccw",
    "timer",
    "flame",
    "snowflake",
    "fan",
    "milk",
    "wheat",
    "leaf",
    "sprout",
    "arrow-up",
    "arrow-down",
    "plus",
    "minus",
    "container",
    "lock",
    "link",
];
export const affordanceIconSchema = z.enum(AFFORDANCE_ICON_NAMES);
export const AFFORDANCE_TYPES = [
    "toggle",
    "setpoint",
    "plot",
    "counter",
    "parameterize",
    "config-form",
    "config-profile",
    "readout",
    "sequence",
];
/**
 * Coarse layout size class. A device may declare it to override the widget's
 * intrinsic size (e.g. promote a plot to `full`); BrewBase maps the class to a
 * grid span. Never absolute geometry — it stays responsive and clamps to the
 * available columns. Omitted = use the widget type's intrinsic class.
 */
export const AFFORDANCE_SIZES = ["compact", "wide", "full"];
export const affordanceSizeSchema = z.enum(AFFORDANCE_SIZES);
/**
 * Generic visibility predicate. A widget (or plot series) declares it to hide
 * itself until a channel reports a satisfying value — e.g. a flow totalizer that
 * only makes sense once the device reports an active calibration. Evaluated
 * fail-closed (no telemetry frame yet ⇒ hidden); defaults: `op = "ne"`, `value = 0`,
 * i.e. "visible when the channel is truthy".
 */
export const visibleWhenSchema = z.object({
    channel: z.string().min(1),
    op: z.enum(["ne", "eq", "gt", "lt"]).optional(),
    value: z.number().optional(),
});
/**
 * "This affordance is currently owned by an external controller." Structurally
 * identical to `visibleWhenSchema` — same `{ channel, op?, value? }` predicate,
 * same evaluator — but a different consumer: when TRUE against live telemetry the
 * widget renders read-only (with a "controlled" badge + live value) instead of
 * disappearing. The signal comes from the controlled device itself (e.g. a pump's
 * `emit.cp_link`); BrewBase stays ignorant of who the controller is. Defaults
 * (`op = "ne"`, `value = 0`) ⇒ "controlled while the channel is truthy".
 */
export const controlledWhenSchema = visibleWhenSchema;
const baseFields = {
    id: z.string().min(1),
    group: z.string().optional(),
    order: z.number().optional(),
    size: affordanceSizeSchema.optional(),
    visible_when: visibleWhenSchema.optional(),
    controlled_when: controlledWhenSchema.optional(),
};
// ── Per-type schemas ──────────────────────────────────────────────────────────
export const toggleAffordanceSchema = z.object({
    ...baseFields,
    type: z.literal("toggle"),
    refs: z.object({ command: z.string().min(1), state: z.string().optional() }),
    params: z
        .object({
        on_icon: affordanceIconSchema.optional(),
        off_icon: affordanceIconSchema.optional(),
        on_label: z.string().optional(),
        off_label: z.string().optional(),
        danger: z.boolean().optional(),
    })
        .default({}),
});
/**
 * One unit in a multi-unit setpoint (e.g. Flow vs Speed on a pump). Each mode
 * writes its own `setpoint.*` register and reads its own `emit.*` live value, so
 * the widget can switch units bumplessly. A `requires_gate` mode is only
 * selectable when the affordance's `refs.gate` channel is truthy.
 */
export const setpointModeSchema = z.object({
    label: z.string().min(1), // selector label ("Speed", "Flow")
    unit: z.string().min(1), // display unit ("rpm", "L/h")
    channel: z.string().min(1), // the `setpoint.*` register this unit writes
    live: z.string().optional(), // `emit.*` channel showing the actual value (display + bumpless seed)
    presets: z.string().optional(), // mode-scoped preset config key (replaces affordance-level presets)
    requires_gate: z.boolean().optional(), // only selectable when `refs.gate` is truthy
});
export const setpointAffordanceSchema = z.object({
    ...baseFields,
    type: z.literal("setpoint"),
    // `presets` references a `list`-typed config key holding {name, value} rows.
    // `gate` enables gated modes (truthiness ≠ 0); `mode` is the channel reporting
    // which register the device currently obeys (maps to a mode via `requires_gate`).
    // `readback` names a channel carrying the live actual value of an externally
    // co-owned setpoint (e.g. a Modbus R/W register the plant HMI also writes). When
    // present the widget shows the readback as the primary value and flags divergence
    // from the commanded value — the numeric analog of toggle's `state`.
    refs: z.object({
        channel: z.string().min(1),
        presets: z.string().optional(),
        gate: z.string().optional(),
        mode: z.string().optional(),
        readback: z.string().optional(),
    }),
    params: z
        .object({
        label: z.string().optional(),
        icon: affordanceIconSchema.optional(),
        step: z.number().optional(),
        // When present, the widget renders a unit selector over these modes instead
        // of the single `refs.channel`. Absent ⇒ unchanged single-channel behavior.
        modes: z.array(setpointModeSchema).optional(),
        // Signed setpoint (negative = reverse, e.g. a pump): the widget edits the
        // magnitude and renders a Fwd/Rev switch that carries the sign.
        direction: z.boolean().optional(),
    })
        .default({}),
});
export const plotAffordanceSchema = z.object({
    ...baseFields,
    type: z.literal("plot"),
    refs: z.object({}).default({}),
    params: z.object({
        window_s: z.number().positive().optional(),
        series: z
            .array(z.object({
            key: z.string().min(1),
            own_axis: z.boolean().optional(),
            color: z.string().optional(),
            // Per-series visibility gate (e.g. hide the flow trace until calibrated).
            visible_when: visibleWhenSchema.optional(),
        }))
            .min(1),
    }),
});
export const counterAffordanceSchema = z.object({
    ...baseFields,
    type: z.literal("counter"),
    refs: z.object({
        channel: z.string().min(1),
        reset: z.string().optional(),
        seed: z.string().optional(),
    }),
    params: z
        .object({
        label: z.string().optional(),
        icon: affordanceIconSchema.optional(),
        precision: z.number().int().min(0).optional(),
    })
        .default({}),
});
export const parameterizeAffordanceSchema = z.object({
    ...baseFields,
    type: z.literal("parameterize"),
    refs: z.object({}).default({}),
    params: z.object({
        // Columns the operator records; `unit` is the canonical unit symbol the
        // entry is converted to (drives the compatible-unit picker via the unit system).
        data_points: z
            .array(z.object({
            name: z.string().min(1),
            unit: z.string().min(1),
            label: z.string().optional(),
        }))
            .min(1),
        // Quantity being fit, expressed over the recorded columns (e.g. "volume / duration").
        response: z.object({ expr: z.string().min(1), unit: z.string().min(1) }),
        // Predicted response, linear in the named parameters (e.g. "a * rpm + b").
        model: z.object({ expr: z.string().min(1) }),
        // Fitted parameters and the config keys they are written to.
        parameters: z
            .array(z.object({ name: z.string().min(1), config_key: z.string().min(1) }))
            .min(1),
    }),
});
/**
 * A self-contained calibration record: the data points, the fitted parameters,
 * and the model that produced them. Snapshotted into a config profile's metadata
 * (under `calibrations[affordanceKey]`) when a fit is applied, so a calibration
 * becomes a retrievable, portable object — viewable read-only after the fact and
 * transferable between schema-compatible devices via copy-paste.
 */
export const calibrationRecordSchema = z.object({
    affordance_key: z.string().min(1),
    model: z.string(),
    response: z.string(),
    // The recorded rows: each column name → the value and unit as entered.
    data_points: z.array(z.record(z.string(), z.object({ value: z.number(), unit: z.string() }))),
    fitted_params: z.record(z.string(), z.number()),
    // param name → the config key it was written to. The compatibility key for
    // transfer: a calibration only applies to a device whose parameterize
    // affordance writes the same set of config keys.
    param_config_keys: z.record(z.string(), z.string()),
    r_squared: z.number().nullable(),
    fitted_at: z.string(), // ISO 8601
});
/** Clipboard envelope for copy-paste calibration transfer. */
export const CALIBRATION_CLIPBOARD_KIND = "brewbase.calibration/v1";
export const calibrationTransferSchema = z.object({
    kind: z.literal(CALIBRATION_CLIPBOARD_KIND),
    // The source profile's name, carried so a paste can keep it.
    name: z.string().optional(),
    record: calibrationRecordSchema,
});
/** Read and validate the calibration records carried in a profile's metadata. */
export function readProfileCalibrations(metadata) {
    const cals = metadata
        ?.calibrations;
    if (!cals || typeof cals !== "object")
        return {};
    const out = {};
    for (const [key, value] of Object.entries(cals)) {
        const parsed = calibrationRecordSchema.safeParse(value);
        if (parsed.success)
            out[key] = parsed.data;
    }
    return out;
}
export const configFormAffordanceSchema = z.object({
    ...baseFields,
    type: z.literal("config-form"),
    refs: z.object({}).default({}),
    params: z
        .object({
        label: z.string().optional(),
        // Explicit config keys; omitted = render inferred (unclaimed scalar) params.
        keys: z.array(z.string()).optional(),
    })
        .default({}),
});
export const configProfileAffordanceSchema = z.object({
    ...baseFields,
    type: z.literal("config-profile"),
    refs: z.object({}).default({}),
    // `select` = compact active-profile picker (an operational top action);
    // `manage` = create/activate/delete (configuration). A device may declare one
    // of each and place them independently. Omitted = `manage` (full panel).
    params: z
        .object({
        label: z.string().optional(),
        mode: z.enum(["select", "manage"]).optional(),
    })
        .default({}),
});
export const readoutAffordanceSchema = z.object({
    ...baseFields,
    type: z.literal("readout"),
    refs: z.object({ channel: z.string().min(1) }),
    params: z
        .object({
        label: z.string().optional(),
        icon: affordanceIconSchema.optional(),
        precision: z.number().int().min(0).optional(),
    })
        .default({}),
});
/**
 * Operator-gated phase machine. The device runs the FSM and owns the guards; this
 * widget just surfaces the current phase (from `refs.channel`, an `emit.*` carrying
 * the phase index) and writes a requested target index to `refs.command` (a
 * `setpoint.*`). The phase vocabulary + edges live in the manifest's top-level
 * `phases[]` (see `phaseSchema`), not here — this affordance only wires the channels.
 *
 * The optional `remaining`/`hold`/`target` refs surface the firmware's dwell timer
 * for the *current* phase (single shared channels, reported per active phase): the
 * widget shows a countdown + progress (`hold`/`target`) on a dwell phase, and treats
 * `target == 0` as a threshold phase (no timer). There is deliberately no readiness
 * ref — readiness is derived client-side from the phase's `guards` plus the timer
 * (`guards all true && (target == 0 || remaining == 0)`), keeping one source of truth.
 */
export const sequenceAffordanceSchema = z.object({
    ...baseFields,
    type: z.literal("sequence"),
    refs: z.object({
        channel: z.string().min(1), // emit.* carrying the current phase index
        command: z.string().min(1), // setpoint.* the widget writes the target index to
        remaining: z.string().optional(), // emit.* countdown (s) to auto-advance; 0 in threshold phases
        hold: z.string().optional(), // emit.* dwell condition sustained time so far (s)
        target: z.string().optional(), // emit.* dwell target for current phase (s); 0 = threshold phase
    }),
    params: z
        .object({
        label: z.string().optional(),
        icon: affordanceIconSchema.optional(),
    })
        .default({}),
});
export const affordanceSchema = z.discriminatedUnion("type", [
    toggleAffordanceSchema,
    setpointAffordanceSchema,
    plotAffordanceSchema,
    counterAffordanceSchema,
    parameterizeAffordanceSchema,
    configFormAffordanceSchema,
    configProfileAffordanceSchema,
    readoutAffordanceSchema,
    sequenceAffordanceSchema,
]);
/**
 * A single phase in a device's sequencer (manifest top-level `phases[]`, ordered —
 * array index = the phase's numeric value, which is what `emit.phase` reports and
 * what the `sequence` affordance writes to its command). `to` lists the phase ids
 * reachable from this phase (the edges to offer as transition buttons); `desc` is
 * operator-facing helper text. The firmware owns the transition guards — a request
 * is accepted only when its guard passes, so a write may be a no-op ("not yet"),
 * never an error. The gateway relays this verbatim; BrewBase stores it first-class.
 *
 * `guards` are the `emit.*` boolean channels that gate advancing *out of* this phase
 * (the firmware still owns enforcement — this is only the declaration BrewBase needs
 * to render the per-condition "why isn't it advancing" checklist). An empty list is a
 * terminal phase (e.g. idle). Whether a phase is a dwell vs threshold phase is not
 * declared here — the widget derives it from the sequence affordance's live `target`
 * timer (`target == 0` ⇒ threshold), keeping a single source of truth.
 */
export const phaseSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    desc: z.string().optional(),
    to: z.array(z.string()).default([]),
    guards: z.array(z.string()).default([]),
});
//# sourceMappingURL=affordances.js.map